import React, { useRef, useImperativeHandle, useEffect } from "react";
import styled from "styled-components";

const InputNumberStyle = styled.div`
    width: 100%;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        pointer-events: none;
        background-color: ${props => props.theme.greyLighter};
    }
    &.error {
        background-color: ${props => props.theme.redLight};
        border-color: ${props => props.theme.redDark};
        &:focus-within {
            border-color: ${props => props.theme.redLight};
            outline: 1px solid ${props => props.theme.redDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    user-select: none;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    background-color: transparent;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
`;

const SymbolStyle = styled.div`
    height: 25px;
    display: flex;
    padding: 0 7px;
    user-select: none;
    align-items: center;
    box-sizing: border-box;
    border-left: 1px solid ${props => props.theme.greyLight};
    &.error {
        border-color: ${props => props.theme.redDark};
    }
`;

const ErrorStyle = styled.p`
    height: 0;
    opacity: 0;
    font-size: 11px;
    visibility: hidden;
    color: ${props => props.theme.redDark};
    transition: opacity 0.3s, height 0.3s, visibility 0.3s;
    &.error {
        opacity: 1;
        height: 11px;
        margin-top: 2px;
        visibility: visible;
    }
`;

export const InputLatLng = props => {

    const { id, ref, name, title, value, symbol, disabled, maxLength, placeholder, textAlign = "left", error, errorVisible, type } = props;

    const inputRef = useRef(null);

    const errorName = name ? name + "ErrorText" : "errorText";

    const disabledClassName = disabled ? "disabled" : "";
    const errorClassName = (errorVisible && error) ? "error" : "";
    const className = [disabledClassName, errorClassName].join(" ");

    useImperativeHandle(ref, () => ({
        get value() {
            return inputRef.current.value;
        },
        focus: () => inputRef.current.focus(),
        writeValue: val => (inputRef.current.value = isNaN(val) ? "" : `${val}`),
        addEventListener: (type, listener) => inputRef.current.addEventListener(type, listener),
        removeEventListener: (type, listener) => inputRef.current.removeEventListener(type, listener)
    }), [inputRef]);

    const toValue = value => {
        const numbers = value.match(/[\d]+[.,]?[\d]*/g)?.map(e => Number(e.replace(",", ".")));
        if (!numbers || numbers.length === 0 || numbers.length > 3) {
            return null;
        }
        let degrees;
        if (numbers.length === 1) {
            [degrees] = numbers;
        } else if (numbers.length === 2) {
            degrees = numbers[0] + numbers[1] / 60;
        } else {
            degrees = numbers[0] + numbers[1] / 60 + numbers[2] / 3600;
        }
        const max = type === "lat" ? 90 : 180;
        if (degrees < -max || degrees > max) {
            return null;
        }
        return degrees;
    };

    useEffect(() => {
        if (value !== toValue(inputRef.current.value)) {
            if (document.activeElement !== inputRef.current) {
                inputRef.current.value = isNaN(value) ? "" : `${value}`;
            }
        }
    }, [value]);

    const onChange = event => props.onChange(toValue(event.target.value));

    return (
        <InputNumberStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <ContainerStyle className={className}>
                <InputStyle
                    id={id}
                    name={name}
                    type="text"
                    ref={inputRef}
                    onChange={onChange}
                    disabled={disabled}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    className={errorClassName}
                    $textAlign={textAlign} />
                {symbol &&
                    <SymbolStyle className={className}>
                        {symbol}
                    </SymbolStyle>
                }
            </ContainerStyle>
            <ErrorStyle
                name={errorName}
                className={typeof error === "string" ? errorClassName : ""}>
                {error}
            </ErrorStyle>
        </InputNumberStyle>
    );
};